import React, { useCallback, useContext, useEffect, useMemo } from "react";
import {
  useParams,
  useNavigate,
  useLocation,
  useSearchParams,
} from "react-router-dom";
import { useChat } from "./State";
import { useSelectedChatChannel } from "./SelectedChannel";
import { ChatChannel } from "../../../../../fay/Api";
import { useChatSettings } from "./Settings";
import useWindowSize from "../../../../../hooks/useWindowSize";

interface ChatChannelURLSelectionContextType {
  navigateToChatChannel: (
    channel: ChatChannel,
    options?: {
      andMarkAsRead?: boolean;
    },
  ) => void;
}

const ChatChannelURLSelectionContext =
  React.createContext<ChatChannelURLSelectionContextType | null>(null);

/**
 * Provider component for conversation URL selection logic
 */
export const ChatChannelURLSelectionProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const { respondentId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { isMobile } = useWindowSize();
  const [searchParams] = useSearchParams();
  const openFirstChat = Boolean(searchParams.get("openFirstChat"));
  const { sortedChannels } = useChat();

  const {
    selectChatChannelByRespondentId,
    clearSelectedChatChannel,
    selectedChatChannel,
    selectedChatChannelLoading,
  } = useSelectedChatChannel();
  const chatSettings = useChatSettings();

  // Helper function to navigate to a chat channel
  const navigateToChatChannel = useCallback(
    (
      channel: ChatChannel,
      options?: {
        andMarkAsRead?: boolean;
      },
    ) => {
      const targetRespondentId = channel.respondent.id;
      const basePath = `chat/${targetRespondentId}`;

      const andMarkAsRead = options?.andMarkAsRead ?? false;
      const shouldMarkRead =
        chatSettings.shouldAutomaticallyMarkSelectedChatChannelAsRead ||
        andMarkAsRead;
      const search = shouldMarkRead
        ? `?should_mark_read=${channel.chat_channel_id}`
        : "";

      navigate({
        pathname: basePath,
        search,
      });
    },
    [navigate, chatSettings],
  );

  const isChatRoute = useMemo(() => {
    const path = location.pathname;
    return path.includes("/chat");
  }, [location.pathname]);

  // Sync URL respondentId to selected chat channel
  useEffect(() => {
    if (respondentId && isChatRoute) {
      selectChatChannelByRespondentId(respondentId);
    }
  }, [respondentId, selectChatChannelByRespondentId, isChatRoute]);

  // Clear selection when navigating away from chat routes
  useEffect(() => {
    if (!isChatRoute) {
      clearSelectedChatChannel();
    }
  }, [isChatRoute, clearSelectedChatChannel]);

  // Handle auto-navigation to first chat if needed
  const shouldNavigateToFirstChat = useMemo(() => {
    return (
      sortedChannels.length > 0 &&
      !selectedChatChannel?.respondent &&
      !selectedChatChannelLoading &&
      (!isMobile || openFirstChat) &&
      isChatRoute &&
      respondentId === undefined
    );
  }, [
    sortedChannels,
    selectedChatChannel,
    selectedChatChannelLoading,
    isMobile,
    openFirstChat,
    isChatRoute,
    respondentId,
  ]);

  useEffect(() => {
    if (shouldNavigateToFirstChat && sortedChannels[0]) {
      navigateToChatChannel(sortedChannels[0]);
    }
  }, [shouldNavigateToFirstChat, sortedChannels, navigateToChatChannel]);

  const contextValue = useMemo(
    () => ({
      navigateToChatChannel,
    }),
    [navigateToChatChannel],
  );

  return (
    <ChatChannelURLSelectionContext.Provider value={contextValue}>
      {children}
    </ChatChannelURLSelectionContext.Provider>
  );
};

/**
 * Hook for accessing the conversation URL selection context
 */
export const useChatChannelURLSelection = () => {
  const context = useContext(ChatChannelURLSelectionContext);
  if (!context) {
    throw new Error(
      "useChatChannelURLSelection must be used within a ChatChannelURLSelectionProvider",
    );
  }
  return context;
};
